<template>
  <div class="page-wrapper">
    <div class="page-head">
      <p class="page-desc">配置健身房在登录页、侧栏与首页展示的名称与 Logo</p>
    </div>

    <div class="settings-card">
      <el-form
        :label-position="isMobile ? 'top' : 'left'"
        :label-width="isMobile ? 'auto' : '100px'"
        class="settings-form"
      >
        <el-form-item label="健身房名称">
          <el-input v-model="form.gymName" placeholder="例如：智伴随健身房管理系统" maxlength="80" show-word-limit />
        </el-form-item>
        <el-form-item label="健身房 Logo">
          <div class="logo-section">
            <div class="logo-preview">
              <img v-if="form.gymLogo" :src="logoPreviewUrl" alt="Logo" />
              <span v-else class="logo-placeholder">未设置</span>
            </div>
            <div class="logo-actions">
              <el-upload
                class="logo-uploader"
                :auto-upload="false"
                :show-file-list="false"
                accept="image/png,image/jpeg,image/gif,image/webp"
                :on-change="onLogoChange"
              >
                <el-button type="primary" plain class="logo-btn">上传图片</el-button>
              </el-upload>
              <el-button v-if="form.gymLogo" type="danger" plain class="logo-btn" @click="form.gymLogo = ''">清除 Logo</el-button>
            </div>
          </div>
          <p class="hint">支持 PNG / JPG / GIF / WebP，建议正方形，大小不超过 800KB</p>
        </el-form-item>
        <el-form-item class="save-item">
          <el-button type="primary" :loading="saving" class="save-btn" @click="save">保存设置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="settings-card notice-card">
      <div class="notice-head">
        <h3>发布通知</h3>
        <p>发布后会显示在教练端和会员端右上角通知中</p>
      </div>
      <el-input
        v-model="noticeText"
        type="textarea"
        :rows="3"
        maxlength="200"
        show-word-limit
        placeholder="请输入通知内容（最多200字）"
      />
      <div class="notice-actions">
        <el-button type="primary" :loading="publishing" class="publish-btn" @click="publishNotice">发布通知</el-button>
      </div>
      <div class="notice-history">
        <div class="notice-history-title">最近通知</div>
        <div v-if="noticeList.length === 0" class="hint">暂无通知</div>
        <div v-for="n in noticeList" :key="n.id" class="notice-row">
          <div class="notice-row-main">
            <p class="notice-row-text">{{ n.text }}</p>
            <p class="notice-row-meta">{{ n.timeLabel || n.createTime }}</p>
          </div>
          <el-button
            type="danger"
            link
            size="small"
            class="notice-row-delete"
            :loading="deletingNoticeId === n.id"
            @click="confirmRemoveNotice(n)"
          >
            删除
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import api from '@/api/request'
import { resolveMediaUrl } from '@/utils/media'

const MAX_LOGO_BYTES = 800 * 1024

export default {
  name: 'AdminView',
  data() {
    return {
      form: { gymName: '', gymLogo: '' },
      saving: false,
      noticeText: '',
      publishing: false,
      noticeList: [],
      deletingNoticeId: null,
      windowWidth: window.innerWidth,
    }
  },
  mounted() {
    this.load()
    this.loadNotices()
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
  },
  computed: {
    logoPreviewUrl() {
      return resolveMediaUrl(this.form.gymLogo)
    },
    isMobile() {
      return this.windowWidth <= 600
    },
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },
    async load() {
      try {
        const res = await api.getSystemSettings()
        if (res.data?.code === 200 && res.data.data) {
          this.form.gymName = res.data.data.gymName || ''
          this.form.gymLogo = res.data.data.gymLogo || ''
        }
      } catch {
        ElMessage.error('加载设置失败')
      }
    },
    async onLogoChange(file) {
      const raw = file.raw
      if (!raw) return
      if (raw.size > MAX_LOGO_BYTES) {
        ElMessage.warning('图片不能超过 800KB')
        return
      }
      try {
        const res = await api.uploadSystemLogo(raw)
        if (res.data?.code === 200 && res.data.data) {
          const d = res.data.data
          this.form.gymName = d.gymName || this.form.gymName
          this.form.gymLogo = d.gymLogo || ''
          this.$store.commit('SET_BRANDING', { gymName: d.gymName, gymLogo: d.gymLogo || '' })
          ElMessage.success('Logo 已上传并保存')
        } else {
          ElMessage.error(res.data?.message || '上传失败')
        }
      } catch {
        ElMessage.error('上传失败')
      }
    },
    async save() {
      if (!this.form.gymName?.trim()) {
        ElMessage.warning('请填写健身房名称')
        return
      }
      this.saving = true
      try {
        const res = await api.saveSystemSettings({
          gymName: this.form.gymName.trim(),
          gymLogo: this.form.gymLogo || '',
        })
        if (res.data?.code === 200) {
          ElMessage.success('保存成功')
          const d = res.data.data || {}
          this.$store.commit('SET_BRANDING', { gymName: d.gymName, gymLogo: d.gymLogo || '' })
        } else {
          ElMessage.error(res.data?.message || '保存失败')
        }
      } catch {
        ElMessage.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async loadNotices() {
      try {
        const res = await api.getNotificationList({ limit: 10 })
        if (res.data?.code === 200 && Array.isArray(res.data.data)) {
          this.noticeList = res.data.data
        }
      } catch {
        /* 忽略加载失败，不影响主要配置 */
      }
    },
    async publishNotice() {
      const text = this.noticeText?.trim()
      if (!text) {
        ElMessage.warning('请输入通知内容')
        return
      }
      this.publishing = true
      try {
        const res = await api.publishNotification(text)
        if (res.data?.code === 200) {
          ElMessage.success('发布成功')
          this.noticeText = ''
          this.loadNotices()
        } else {
          ElMessage.error(res.data?.message || '发布失败')
        }
      } catch {
        ElMessage.error('发布失败')
      } finally {
        this.publishing = false
      }
    },
    confirmRemoveNotice(n) {
      if (!n?.id) return
      ElMessageBox.confirm('确定删除这条通知吗？删除后教练端与会员端将不再显示。', '删除通知', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => this.removeNotice(n.id))
        .catch(() => {})
    },
    async removeNotice(id) {
      this.deletingNoticeId = id
      try {
        const res = await api.removeNotification(id)
        if (res.data?.code === 200) {
          ElMessage.success('已删除')
          this.loadNotices()
        } else {
          ElMessage.error(res.data?.message || '删除失败')
        }
      } catch {
        ElMessage.error('删除失败')
      } finally {
        this.deletingNoticeId = null
      }
    },
  },
}
</script>

<style scoped>
.page-wrapper {
  padding: 0;
  max-width: 720px;
}
.page-head {
  margin-bottom: 20px;
}
.page-desc {
  margin: 0;
  font-size: 14px;
  color: var(--text-muted);
}
.settings-card {
  background: var(--bg-white);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 28px 24px;
  margin-bottom: 16px;
}
.settings-form {
  max-width: 560px;
}
/* Logo 布局：预览 + 操作按钮组 */
.logo-section {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.logo-preview {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border: 1px dashed var(--border-color);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--bg-gray);
}
.logo-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.logo-placeholder {
  font-size: 12px;
  color: var(--text-muted);
}
.logo-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding-top: 4px;
}
.hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.6;
}
.save-item {
  margin-bottom: 0;
}
.notice-card {
  margin-bottom: 0;
}
.notice-head h3 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}
.notice-head p {
  margin: 6px 0 12px;
  font-size: 13px;
  color: var(--text-muted);
}
.notice-actions {
  margin-top: 12px;
}
.notice-history {
  margin-top: 16px;
  border-top: 1px solid var(--border-light);
  padding-top: 12px;
}
.notice-history-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
}
.notice-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light);
}
.notice-row-main {
  flex: 1;
  min-width: 0;
}
.notice-row-delete {
  flex-shrink: 0;
  margin-top: 2px;
}
.notice-row:last-child {
  border-bottom: none;
}
.notice-row-text {
  margin: 0 0 4px;
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.5;
}
.notice-row-meta {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
}

/* ===== 移动端适配 ===== */
@media (max-width: 600px) {
  .settings-card {
    padding: 18px 16px;
    border-radius: var(--radius-md);
  }
  .settings-form {
    max-width: 100%;
  }
  /* 标签在 top 模式下减小间距 */
  :deep(.el-form-item__label) {
    font-size: 13px;
    padding-bottom: 6px;
  }
  :deep(.el-form-item) {
    margin-bottom: 18px;
  }
  /* Logo 区域竖向排列 */
  .logo-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .logo-preview {
    width: 72px;
    height: 72px;
  }
  .logo-actions {
    padding-top: 0;
  }
  .logo-btn {
    height: 36px;
    padding: 0 16px;
    font-size: 13px;
  }
  /* 保存 / 发布按钮撑满宽度 */
  .save-btn,
  .publish-btn {
    width: 100%;
  }
  .save-item :deep(.el-form-item__content) {
    display: block;
  }
  .notice-head h3 {
    font-size: 16px;
  }
}

@media (max-width: 360px) {
  .settings-card {
    padding: 14px 12px;
  }
}
</style>
